<template>

  <div class="componente-completo" ref="containerRef">
    <img ref="bolaRef" src="@/assets/disco.png" alt="Bola de disco" class="bola-disco" />
    <img ref="estrellasRef" src="@/assets/estrellas.png" alt="Estrellas" class="estrellas-decorativas" />
    <img ref="fotoRef" src="@/assets/ovalada1.webp" alt="Foto destacada" class="foto-elegante" />
  </div>
</template>
<script>
import { ref, onMounted } from 'vue';
import gsap from 'gsap';

export default {
  name: 'ComponenteCompleto',
  setup() {
    const containerRef = ref(null);
    const fotoRef = ref(null);
    const foto2Ref = ref(null);
    const bolaRef = ref(null);
    const estrellasRef = ref(null);
    const waveSvg = ref(null);
    const wavePathStroke = ref(null);
    const wavePathFill = ref(null);

    const animate = () => {
      const tl = gsap.timeline({
        defaults: { ease: 'power3.out' }
      });

      // Si tienes la wave en otro lado, la respetamos sin tronar nada
      if (waveSvg.value && wavePathStroke.value && wavePathFill.value) {
        const pathLength = wavePathStroke.value.getTotalLength();
        wavePathStroke.value.style.strokeDasharray = pathLength;
        wavePathStroke.value.style.strokeDashoffset = pathLength;

        tl.fromTo(
          waveSvg.value,
          { opacity: 0, y: -60 },
          { opacity: 1, y: 0, duration: 1 }
        )
          .to(
            wavePathStroke.value,
            { strokeDashoffset: 0, duration: 1.4 },
            '-=0.6'
          )
          .to(
            wavePathFill.value,
            { opacity: 1, duration: 1 },
            '-=1'
          );
      }

      // 1) ENTRADA GENERAL DEL CONTENEDOR (blur + zoom)
      tl.fromTo(
        containerRef.value,
        {
          opacity: 0,
          y: 60,
          scale: 0.96,
          filter: 'blur(10px)'
        },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          filter: 'blur(0px)',
          duration: 1.1
        },
        '-=0.4'
      );

      // 2) FOTO PRINCIPAL – EFECTO 3D + SLIDE
      if (fotoRef.value) {
        tl.fromTo(
          fotoRef.value,
          {
            opacity: 0,
            y: 80,
            rotateX: 35,
            rotateZ: -10,
            scale: 0.9,
            transformOrigin: 'center center'
          },
          {
            opacity: 1,
            y: 0,
            rotateX: 0,
            rotateZ: -4,
            scale: 1,
            duration: 1.2
          },
          '-=0.8'
        );
      }

      // 3) SEGUNDA FOTO (si la agregas después)
      if (foto2Ref.value) {
        tl.fromTo(
          foto2Ref.value,
          {
            opacity: 0,
            y: 70,
            rotateX: 35,
            rotateZ: 10,
            scale: 0.9,
            transformOrigin: 'center center'
          },
          {
            opacity: 1,
            y: 0,
            rotateX: 0,
            rotateZ: 4,
            scale: 1,
            duration: 1.1
          },
          '-=1.0'
        );
      }

      // 4) BOLA DE DISCO – CAE DEL CIELO, REBOTA Y SE QUEDA FLOJITA
      if (bolaRef.value) {
        tl.fromTo(
          bolaRef.value,
          {
            opacity: 0,
            y: -140,
            scale: 0.5,
            rotate: -15,
            transformOrigin: 'center'
          },
          {
            opacity: 1,
            y: 0,
            scale: 1,
            rotate: 0,
            duration: 1,
            ease: 'bounce.out'
          },
          '-=1'
        );

        // Movimiento suave en loop (flotando)
        gsap.to(bolaRef.value, {
          y: '+=10',
          duration: 2.4,
          yoyo: true,
          repeat: -1,
          ease: 'sine.inOut'
        });
        gsap.to(bolaRef.value, {
          rotation: 4,
          duration: 5,
          yoyo: true,
          repeat: -1,
          ease: 'sine.inOut'
        });
      }

      // 5) ESTRELLAS – APARECEN Y SE QUEDAN BRILLANDO
      if (estrellasRef.value) {
        tl.fromTo(
          estrellasRef.value,
          {
            opacity: 0,
            y: -40,
            scale: 0.8
          },
          {
            opacity: 1,
            y: 0,
            scale: 1,
            duration: 1,
            ease: 'power2.out'
          },
          '-=0.9'
        );

        // Twinkle / parpadeo suave
        gsap.to(estrellasRef.value, {
          opacity: 0.4,
          duration: 1.5,
          yoyo: true,
          repeat: -1,
          ease: 'sine.inOut'
        });
        gsap.to(estrellasRef.value, {
          scale: 1.05,
          duration: 2,
          yoyo: true,
          repeat: -1,
          ease: 'sine.inOut'
        });
      }
    };

    onMounted(() => {
      const observer = new IntersectionObserver(
        ([entry], obs) => {
          if (entry.isIntersecting) {
            animate();
            obs.unobserve(entry.target);
          }
        },
        { threshold: 0.3 }
      );

      if (containerRef.value) observer.observe(containerRef.value);
    });

    return {
      containerRef,
      fotoRef,
      foto2Ref,
      bolaRef,
      estrellasRef,
      waveSvg,
      wavePathStroke,
      wavePathFill
    };
  }
};
</script>


<style scoped>
.componente-completo {
  position: relative;
  width: 100%;
  padding: 0px 0 160px;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow: hidden;
  margin-top:-20px;
}

.animated-wave {
  width: 100%;
  height: auto;
  pointer-events: none;
  opacity: 0;
}

.wave-path-stroke {
  fill: none;
  stroke: #ffffff00;
  stroke-width: 2.5;
}

.wave-path-fill {
  opacity: 0;
}

.bola-disco {
  width: 120px;
  margin-top: 0;
  transform-origin: center;
  opacity: 0;
  z-index: 1;
  margin-right: -180px;
  margin-bottom: -30px;
}

.estrellas-decorativas {
  width: 100px;
  position: absolute;
  bottom: 30px;
  right: 30px;
  opacity: 0;
  z-index: 0;
}

.foto-elegante,
.foto-elegante-reversa {
  width: 470px;
  max-width: 90%;
  border-radius: 10px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.35);
  opacity: 0;
  transform-origin: center;
  margin: 30px 0;
}

.foto-elegante {
  transform: rotate(-4deg);
  z-index: 2;
}

.foto-elegante-reversa {
  transform: rotate(4deg);
  z-index: 1;
}
</style>
